/**
 * Slice 44 — the Meta ad surfaces (Facebook and Instagram then, WhatsApp
 * Status since slice 46), read where the rest of the product draws surfaces: one campaign over its lifetime (its draft's page), and
 * the ad account over a period somebody picked (Analytics, and the Results by
 * surface report).
 *
 * ## THE SAME FIGURES AS SLICE 41, AND THE SAME TRAPS
 *
 * {@link metaAdsSurfaceFiguresSchema} is slice 41's account read cut down to
 * what a surface needs: the whole, and the whole broken down by placement.
 * `figures.reach` is Meta's own unbroken count — somebody who saw the ad on
 * Facebook and on Instagram is one person there, and two in `byPlacement`.
 * Nothing adds the placements' reach up; nothing here has a field to put the
 * sum in. Spend, impressions and clicks do add up, and a screen may compare
 * them with a bar.
 *
 * `other` — Audience Network, Messenger and whatever Meta adds — is a
 * placement and not a surface (`surfaceOfPlacement` answers `null`). Its
 * spend is real, so it stays in `byPlacement` and is drawn as itself; a
 * per-surface view that dropped it would lose money from the page.
 *
 * ## A PERIOD IS CALENDAR DAYS IN THE AD ACCOUNT'S ZONE
 *
 * Meta buckets insights by the ad account's reporting zone, and takes a period
 * as two inclusive dates in it. So {@link metaAdsPeriodSchema} is the two days
 * a person picked, read in **that** zone — not the browser's, and not the
 * server's. When the two zones differ, the ad figures cover the same dates as
 * the email and WhatsApp figures beside them, but not the same instants; the
 * screen tags the zone, and nothing adds a figure from one to the other.
 *
 * The longest period is the report window's cap, {@link MAX_REPORT_WINDOW_DAYS}:
 * the two places that ask for one are Analytics and the report, and neither
 * offers more.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import { calendarDateSchema, parseCalendarDate } from './analytics.js';
import { metaAdsTransportSchema } from './meta-ads.js';
import {
  META_PLACEMENT_VALUES,
  metaAdsFiguresSchema,
  metaAdsPlatformFiguresSchema,
  metaAdsReadFailureSchema,
  type MetaPlacement,
} from './meta-ads-read.js';
import { MAX_REPORT_WINDOW_DAYS } from './report.js';
import { surfaceOfPlacement, type Surface } from './surface.js';

/**
 * The one Meta placement value an ad surface is, or `null` for a surface that
 * is not an ad's — the inverse of `surfaceOfPlacement`, derived from it rather
 * than written out again.
 *
 * Slice 52, gap 4 needs it on the server: the drafts list filters by surface
 * in SQL, over the placement values stored in a draft's `targeting`, and a
 * hand-written second map would drift from the one the campaigns table's own
 * surface column uses. Derived, the two cannot.
 */
export function metaPlacementOfSurface(surface: Surface): MetaPlacement | null {
  return META_PLACEMENT_VALUES.find((placement) => surfaceOfPlacement(placement) === surface) ?? null;
}

/** Whole days from `since` to `until`, both included — `null` when either is not a real day. */
export function metaAdsPeriodDays(since: string, until: string): number | null {
  const from = parseCalendarDate(since);
  const to = parseCalendarDate(until);
  if (from === null || to === null) return null;
  const days = (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / 86_400_000;
  return days + 1;
}

/** Two calendar days, both included, and a zone they are read in — refused past the cap or out of order. */
function periodIssues(
  period: { readonly since: string; readonly until: string },
  ctx: z.RefinementCtx,
  paths: { readonly since: string; readonly until: string },
): void {
  const days = metaAdsPeriodDays(period.since, period.until);
  if (days === null) return;
  if (days < 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [paths.until], message: 'The last day cannot be before the first' });
  } else if (days > MAX_REPORT_WINDOW_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [paths.until],
      message: `A period covers at most ${MAX_REPORT_WINDOW_DAYS} days`,
    });
  }
}

export const metaAdsPeriodSchema = z
  .object({
    /** First day included, `YYYY-MM-DD`, in `timezone`. */
    since: calendarDateSchema,
    /** Last day included, `YYYY-MM-DD`, in `timezone`. */
    until: calendarDateSchema,
    /** The ad account's reporting zone — the zone Meta buckets insights in. */
    timezone: z.string().min(1),
  })
  .strict()
  .superRefine((period, ctx) => periodIssues(period, ctx, { since: 'since', until: 'until' }));
export type MetaAdsPeriod = z.infer<typeof metaAdsPeriodSchema>;

/** The whole, and the whole by placement. See the header for why `reach` is only ever the whole's. */
export const metaAdsSurfaceFiguresSchema = z
  .object({
    transport: metaAdsTransportSchema,
    readAt: isoDateTimeSchema,
    currency: z.string().length(3),
    /** Meta's own unbroken figures — reach with the overlap removed. */
    figures: metaAdsFiguresSchema,
    /** Facebook and Instagram always, as zeros when idle; WhatsApp status (slice 46) and `other` only when something is in them. */
    byPlacement: z.array(metaAdsPlatformFiguresSchema),
  })
  .strict();
export type MetaAdsSurfaceFigures = z.infer<typeof metaAdsSurfaceFiguresSchema>;

/** The ad account over one period. */
export const metaAdsPeriodReadSchema = z
  .object({
    transport: metaAdsTransportSchema,
    readAt: isoDateTimeSchema,
    currency: z.string().length(3),
    figures: metaAdsFiguresSchema,
    byPlacement: z.array(metaAdsPlatformFiguresSchema),
    period: metaAdsPeriodSchema,
  })
  .strict();
export type MetaAdsPeriodRead = z.infer<typeof metaAdsPeriodReadSchema>;

/**
 * What `GET /meta-ads/period` answers with — the account read's states, and the
 * same reason for each: an unreadable account is not an empty one, so only
 * `ok` carries figures.
 */
export const metaAdsPeriodStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('ok'), read: metaAdsPeriodReadSchema }).strict(),
  z.object({ state: z.literal('not_connected') }).strict(),
  z.object({ state: z.literal('not_checked') }).strict(),
  z.object({ state: z.literal('not_permitted') }).strict(),
  z.object({ state: z.literal('failed'), failure: metaAdsReadFailureSchema }).strict(),
]);
export type MetaAdsPeriodState = z.infer<typeof metaAdsPeriodStateSchema>;

/**
 * What `GET /meta-ads/drafts/:id/results` answers with: one published
 * campaign over its lifetime, in the states the draft's delivery read has —
 * including `elsewhere`, a campaign in an ad account that is not the one
 * connected now, which is not read through this one.
 */
export const metaAdResultsStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('ok'), results: metaAdsSurfaceFiguresSchema }).strict(),
  z.object({ state: z.literal('not_published') }).strict(),
  z.object({ state: z.literal('not_connected') }).strict(),
  z.object({ state: z.literal('not_checked') }).strict(),
  z.object({ state: z.literal('not_permitted') }).strict(),
  z.object({ state: z.literal('elsewhere'), adAccountId: z.string() }).strict(),
  z.object({ state: z.literal('failed'), failure: metaAdsReadFailureSchema }).strict(),
]);
export type MetaAdResultsState = z.infer<typeof metaAdResultsStateSchema>;

/**
 * `GET /meta-ads/period?fromDate=&toDate=` — the two days Analytics' period
 * picker holds, both included. No zone: the API reads them in the ad
 * account's.
 */
export const metaAdsPeriodQuerySchema = z
  .object({
    fromDate: calendarDateSchema,
    toDate: calendarDateSchema,
  })
  .strict()
  .superRefine((query, ctx) =>
    periodIssues({ since: query.fromDate, until: query.toDate }, ctx, { since: 'fromDate', until: 'toDate' }),
  );
export type MetaAdsPeriodQuery = z.infer<typeof metaAdsPeriodQuerySchema>;
